import { useEffect, useRef, useState } from 'react';
import { SlidersHorizontal, X } from 'lucide-react';
import clsx from 'clsx';
import { effectiveRange, effectiveValues, isNarrowed, type BulkDistribution, type ParamBulkSpec } from '../../core/params/bulk';
import { paramBulkRange, type Param } from '../../core/params/types';
import { RangeSlider } from '../controls/RangeSlider';
import { Segmented } from '../controls/Basics';

const DISTRIBUTIONS: { value: BulkDistribution; label: string; title: string }[] = [
  { value: 'uniform', label: 'Gleich', title: 'Alle Werte im Bereich gleich wahrscheinlich' },
  { value: 'normal', label: 'Normal', title: 'Häufung um die Bereichsmitte' },
  { value: 'exponential', label: 'Expo', title: 'Häufung am unteren Bereichsende' },
];

/**
 * Editor for a parameter's exploration range.
 *
 * The range is not batch-only configuration: it bounds the randomise buttons straight
 * away, so narrowing it has an immediate, checkable effect. The batch engine later
 * reads exactly the same setting.
 */
export function BulkRangePopover({
  param,
  spec,
  currentValue,
  onChange,
}: {
  param: Param;
  spec: ParamBulkSpec | undefined;
  currentValue: number | string | undefined;
  onChange: (spec: ParamBulkSpec | undefined) => void;
}): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const numeric = param.kind === 'float' || param.kind === 'int';
  const discrete = param.kind === 'enum' || param.kind === 'text';
  if (!numeric && !discrete) return null;
  if (param.neverRandomise) return null;

  const narrowed = isNarrowed(param, spec);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        title="Bereich für Randomize und Batch festlegen"
        onClick={() => setOpen((v) => !v)}
        className={clsx(
          'ghost-button h-5 w-5 p-0 transition-opacity',
          narrowed || open ? 'text-accent-500 opacity-100' : 'opacity-0 group-hover/slider:opacity-100 group-hover/param:opacity-100',
        )}
      >
        <SlidersHorizontal size={11} />
      </button>

      {open && (
        <div className="absolute right-0 top-6 z-50 w-[248px] rounded-lg border border-ink-700 bg-ink-850 p-2.5 shadow-2xl shadow-black/60">
          <div className="mb-2 flex items-center gap-2">
            <span className="panel-label flex-1">Explorationsbereich</span>
            <button type="button" className="ghost-button h-5 w-5 p-0" onClick={() => setOpen(false)}>
              <X size={11} />
            </button>
          </div>

          {numeric && (
            <>
              <RangeSlider
                value={effectiveRange(param, spec)}
                onChange={(range) => onChange({ ...spec, range })}
                min={param.min}
                max={param.max}
                step={param.step}
                scale={param.scale}
                marker={typeof currentValue === 'number' ? currentValue : undefined}
              />
              <div className="mb-2 flex justify-between font-mono text-[10px] text-ink-400">
                <span>{effectiveRange(param, spec)[0]}</span>
                <span className="text-ink-600">{param.unit}</span>
                <span>{effectiveRange(param, spec)[1]}</span>
              </div>

              <div className="mb-1 text-[10px] text-ink-500">Verteilung</div>
              <Segmented
                size="sm"
                value={spec?.distribution ?? 'uniform'}
                onChange={(distribution) => onChange({ ...spec, distribution })}
                options={DISTRIBUTIONS}
              />
              {spec?.distribution === 'normal' && (
                <div className="mt-2">
                  <div className="mb-1 text-[10px] text-ink-500">Streuung σ — {(spec.sigma ?? 0.25).toFixed(2)}</div>
                  <RangeSlider
                    value={[0, spec.sigma ?? 0.25]}
                    onChange={([, sigma]) => onChange({ ...spec, sigma })}
                    min={0.02}
                    max={0.6}
                    step={0.01}
                    height={16}
                  />
                </div>
              )}
            </>
          )}

          {param.kind === 'enum' && (
            <div className="flex flex-wrap gap-1">
              {param.options.map((option) => {
                const allowed = effectiveValues(param, spec);
                const active = allowed.includes(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      const next = active
                        ? allowed.filter((v) => v !== option.value)
                        : [...allowed, option.value];
                      // Never leave an empty set — that would make the parameter
                      // silently unrandomisable rather than obviously constrained.
                      onChange(next.length ? { ...spec, values: next } : { ...spec, values: undefined });
                    }}
                    className={clsx(
                      'rounded px-1.5 py-0.5 text-[10px] transition-colors',
                      active ? 'bg-ink-700 text-ink-100' : 'bg-ink-900 text-ink-500 hover:text-ink-300',
                    )}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          )}

          {param.kind === 'text' && (
            <textarea
              rows={4}
              spellCheck={false}
              placeholder={'Ein Wert pro Zeile —\ndie Varianten für Randomize und Batch'}
              value={(spec?.values ?? param.bulkValues ?? []).join('\n')}
              onChange={(e) => {
                const values = e.target.value.split('\n').filter((v) => v.trim().length > 0);
                onChange({ ...spec, values: values.length ? values : undefined });
              }}
              className="field-input resize-y font-mono text-[11px]"
            />
          )}

          <button
            type="button"
            onClick={() => {
              onChange(spec?.locked ? { locked: true } : undefined);
              setOpen(false);
            }}
            className="mt-2 w-full rounded bg-ink-900 py-1 text-[10px] text-ink-400 transition-colors hover:text-ink-200"
          >
            Auf Standardbereich zurücksetzen
            {numeric && (
              <span className="ml-1 font-mono text-ink-600">
                ({paramBulkRange(param)[0]} … {paramBulkRange(param)[1]})
              </span>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
